import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// Layout
import MainLayout from '@/components/layout/MainLayout';

// Pages
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import SSOCallback from '@/pages/auth/SSOCallback';
import DentuluCallback from '@/pages/auth/DentuluCallback';
import Dashboard from '@/pages/dashboard/Dashboard';
import PatientList from '@/pages/patients/PatientList';
import PatientDetail from '@/pages/patients/PatientDetail';
import AddPatient from '@/pages/patients/AddPatient';
import EditPatient from '@/pages/patients/EditPatient';
import ChartEditor from '@/pages/charting/ChartEditor';
import ChartHistory from '@/pages/charting/ChartHistory';
import ChartList from '@/pages/charting/ChartList';
import ExternalChartView from '@/pages/charting/ExternalChartView';
import ExternalChartEditor from '@/pages/charting/ExternalChartEditor';
import Settings from '@/pages/settings/Settings';
import TermsOfService from '@/pages/legal/TermsOfService';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import HipaaNotice from '@/pages/legal/HipaaNotice';

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Debug: Log auth state on every render
  // eslint-disable-next-line no-console
  console.log('ProtectedRoute: isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'authStore:', window.localStorage.getItem('perio-chart-auth'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirects if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Debug: Log auth state on every render
  // eslint-disable-next-line no-console
  console.log('PublicRoute: isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'authStore:', window.localStorage.getItem('perio-chart-auth'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route path="/sso/callback" element={<SSOCallback />} />
        <Route path="/dentulu-callback" element={<DentuluCallback />} />
        
        {/* External/Public chart viewing and editing - no authentication required */}
        <Route path="/external/charts/:chartId" element={<ExternalChartView />} />
        <Route path="/external/charts/:chartId/edit" element={<ExternalChartEditor />} />
        
        {/* Legal pages - public access */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/hipaa" element={<HipaaNotice />} />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          
          {/* Patients */}
          <Route path="patients" element={<PatientList />} />
          <Route path="patients/add" element={<AddPatient />} />
          <Route path="patients/:patientId" element={<PatientDetail />} />
          <Route path="patients/:patientId/edit" element={<EditPatient />} />
          
          {/* Charting */}
          <Route path="charts" element={<ChartList />} />
          <Route path="charts/new" element={<ChartEditor />} />
          <Route path="charts/:chartId" element={<ChartEditor />} />
          <Route path="charts/:chartId/history" element={<ChartHistory />} />
          <Route path="patients/:patientId/charts" element={<ChartHistory />} />
          <Route path="patients/:patientId/charts/new" element={<ChartEditor />} />
          
          {/* Settings */}
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* 404 */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center min-h-screen">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">404</h1>
              <p className="text-gray-600 mb-4">Page not found</p>
              <a href="/" className="btn-primary btn-md">
                Go Home
              </a>
            </div>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
